// Terrain tile types. The numeric order matches the original WinBolo tile
// enumeration so saved maps round-trip without remapping.
export enum Terrain {
  DeepSea = 0,
  BoatOrBuilding = 1,
  River = 2,
  Swamp = 3,
  Crater = 4,
  Road = 5,
  Forest = 6,
  Rubble = 7,
  Grass = 8,
  Wall = 9,
  HalfBuiltWall = 10,
  Boat = 11,
  Base = 12,
  Pillbox = 13,
}

// Per-terrain movement speed multipliers (1.0 = full tank speed on road).
// Numbers chosen to mirror the ratios in WinBolo's bolo_map.h:
//   ROAD/BOAT/REFBASE = 16, GRASS = 12, FOREST = 6, RIVER/SWAMP/CRATER/RUBBLE = 3,
//   BUILDING/HALFBUILDING/PILLBOX = 0.
// Deep sea and river handle the onBoat case in physics — these multipliers
// are the *not-on-boat* values.
export const TERRAIN_SPEED: Readonly<Record<Terrain, number>> = {
  [Terrain.DeepSea]: 0,            // not-on-boat: blocked (drowning is its own mechanic)
  [Terrain.BoatOrBuilding]: 0,
  [Terrain.River]: 0.2,            // not-on-boat: slow wade (3/16); full when onBoat
  [Terrain.Swamp]: 0.2,
  [Terrain.Crater]: 0.2,
  [Terrain.Road]: 1.0,
  [Terrain.Forest]: 0.4,           // 6/16
  [Terrain.Rubble]: 0.2,
  [Terrain.Grass]: 0.75,           // 12/16
  [Terrain.Wall]: 0,
  [Terrain.HalfBuiltWall]: 0,      // matches HALFBUILDING = 0; blocking
  [Terrain.Boat]: 1.0,
  [Terrain.Base]: 1.0,
  [Terrain.Pillbox]: 0,
};

// Per-terrain MAN/builder speed multipliers (1.0 = full BUILDER_SPEED on
// road/grass/boat/base). Numbers mirror the ratios in WinBolo's bolo_map.h
// MAP_MANSPEED_T* table (lines 89-102), with ROAD = 16 as the denominator:
//   ROAD/GRASS/BOAT/REFBASE = 16, FOREST = 8, SWAMP/CRATER/RUBBLE = 4,
//   DEEPSEA/RIVER/BUILDING/HALFBUILDING/PILLBOX = 0 (blocked).
// A 0 here means the builder can't enter the tile at all — passability
// derives from this table, so adding/removing a terrain only needs an
// edit in one place.
export const BUILDER_TERRAIN_SPEED: Readonly<Record<Terrain, number>> = {
  [Terrain.DeepSea]: 0,
  [Terrain.BoatOrBuilding]: 0,
  [Terrain.River]: 0,
  [Terrain.Swamp]: 0.25,          // 4/16
  [Terrain.Crater]: 0.25,
  [Terrain.Road]: 1.0,
  [Terrain.Forest]: 0.5,          // 8/16
  [Terrain.Rubble]: 0.25,
  [Terrain.Grass]: 1.0,
  [Terrain.Wall]: 0,
  [Terrain.HalfBuiltWall]: 0,
  [Terrain.Boat]: 1.0,
  [Terrain.Base]: 1.0,
  [Terrain.Pillbox]: 0,
};

// Per-terrain tank turn-rate multipliers (1.0 = full turnRate from
// TankTuning, so TURBO and any future style scale on top of this). Direct
// port of bolo_map.h:107-119 MAP_TURN_T* (ROAD/GRASS/BOAT/REFBASE = 1.0,
// FOREST/DEEPSEA = 0.5, RIVER/SWAMP/CRATER/RUBBLE = 0.25). On hard tiles
// the tank can't even be there, so the 0 is academic — included for
// completeness.
export const TANK_TERRAIN_TURN: Readonly<Record<Terrain, number>> = {
  [Terrain.DeepSea]: 0.5,
  [Terrain.BoatOrBuilding]: 0,
  [Terrain.River]: 0.25,
  [Terrain.Swamp]: 0.25,
  [Terrain.Crater]: 0.25,
  [Terrain.Road]: 1.0,
  [Terrain.Forest]: 0.5,
  [Terrain.Rubble]: 0.25,
  [Terrain.Grass]: 1.0,
  [Terrain.Wall]: 0,
  [Terrain.HalfBuiltWall]: 0,
  [Terrain.Boat]: 1.0,
  [Terrain.Base]: 1.0,
  [Terrain.Pillbox]: 0,
};

export interface MapPillbox {
  x: number;
  y: number;
  owner: number; // 0 = neutral, 1..MAX_PLAYERS
  armor: number;
  speed: number;
}

export interface MapBase {
  x: number;
  y: number;
  owner: number;
  armor: number;
  shells: number;
  mines: number;
}

export interface MapStart {
  x: number;
  y: number;
  direction: number;
}

export interface WorldMap {
  name: string;
  author: string;
  // Row-major: terrain[y * MAP_WIDTH + x].
  terrain: Uint8Array;
  pillboxes: MapPillbox[];
  bases: MapBase[];
  starts: MapStart[];
  // Neutral mines authored into the map (e.g. the mine-overlay nibbles in
  // WinBolo's binary format). Optional because procedurally-generated maps
  // don't set any.
  mines?: { x: number; y: number }[];
}
